import { zodResolver } from '@hookform/resolvers/zod'
import { Share, Sms } from 'iconsax-react'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { ProfilePictureCaptureDialog } from '@/components/ProfilePictureCaptureDialog/ProfilePictureCaptureDialog'
import { ProfilePicturePreview } from '@/components/ProfilePictureCaptureDialog/ProfilePicturePreview'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { WeScreen } from '@/components/WeScreen/WeScreen'
import { AppRoutes } from '@/constants/appRoutes'
import { catchWithSentry } from '@/hooks/useCatchWithSentry'
import { useInitProspectSessionMutation } from '@/store/api/PublicForm/PublicForm.query'
import { convertToBase64 } from '@/utils/binary'
import { SocialMirrorPalette } from '@/utils/palette/SocialMirrorPalette'
import type { CreateProspectSchemaType } from '@/utils/schemas'
import { createProspectSchema } from '@/utils/schemas'

const LS_KEY = 'prospect_otp_sent_at'

export const RevealDigitalFootprint = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const language = i18n.language

  // Framer landing can deep-link a product family (?product=impersonation);
  // threaded through the funnel so the paywall shows the matching tiers
  const [searchParams] = useSearchParams()
  const product = searchParams.get('product') || undefined

  const [profilePic, setProfilePic] = useState<File | undefined>(undefined)
  const [displayImageUrl, setDisplayImageUrl] = useState<string>('')

  const [initProspectSession, { isLoading }] = useInitProspectSessionMutation()

  const form = useForm<CreateProspectSchemaType>({
    resolver: zodResolver(createProspectSchema),
    defaultValues: {
      email: '',
      numberOfSocialAccounts: undefined,
      termsAccepted: undefined,
      privacyPolicyAccepted: undefined,
    },
    mode: 'onChange',
  })

  const { handleSubmit, control, formState } = form

  const isFormValid = formState.isValid && profilePic !== undefined

  const handleImageChange = (file: File | undefined) => {
    if (file) {
      setProfilePic(file)
      const blobUrl = URL.createObjectURL(file)
      setDisplayImageUrl(blobUrl)
    }
  }

  const handleCreateProspect = async (data: CreateProspectSchemaType) => {
    try {
      const base64Image = await convertToBase64(profilePic as File)

      if (!base64Image) {
        toast.error(t('public.revealDigitalFootprint.errorMessage'))
        return
      }

      await initProspectSession({
        email: data.email,
        profilePhoto: base64Image,
        numberOfSocialAccounts: data.numberOfSocialAccounts,
        language: language,
      }).unwrap()

      localStorage.setItem(LS_KEY, Date.now().toString())

      toast.success(t('public.revealDigitalFootprint.successMessage'))

      navigate(AppRoutes.prospectEmailVerification, {
        state: { email: data.email, product },
      })
    } catch (error: any) {
      toast.error(t('public.revealDigitalFootprint.errorMessage'))
      catchWithSentry(error)
    }
  }

  return (
    <WeScreen useHeader={false} customViewStyles="max-md:p-0">
      <div className="flex flex-col gap-6 w-full h-full items-center lg:justify-center py-8">
        <div className="flex flex-col gap-3 w-full max-w-2xl text-center">
          <h1 className="text-[40px] max-sm:text-2xl font-bold text-neutralColor80 leading-tight">
            {t('public.revealDigitalFootprint.title')}
          </h1>
          <p className="text-neutralColor60 text-lg max-sm:text-sm font-normal px-4">
            {t('public.revealDigitalFootprint.subtitle')}
          </p>
        </div>

        <div className="w-full max-w-xl bg-baseColorHighlight rounded-3xl max-md:p-3 p-6 shadow-md border border-neutralColor10">
          <Form {...form}>
            <form
              className="w-full flex flex-col gap-6"
              onSubmit={handleSubmit(handleCreateProspect)}
            >
              <div className="flex items-center w-full justify-between max-md:flex-col max-md:gap-3">
                <ProfilePicturePreview
                  imageUrl={displayImageUrl}
                  size="medium"
                />
                <div className="flex flex-col gap-2 w-2/3">
                  <div className="flex flex-col gap-2 w-full">
                    <ProfilePictureCaptureDialog
                      onImageChange={handleImageChange}
                      defaultTab="upload"
                      trigger={
                        <Button
                          type="button"
                          variant="outline"
                          className="flex-1 rounded-xl h-10 text-sm font-semibold border-neutralColor10"
                        >
                          {t(
                            'components.profilePictureCaptureDialog.uploadImage',
                          )}
                        </Button>
                      }
                    />
                    <p className="text-neutralColor60 text-xs italic text-center">
                      {t('components.profilePictureCaptureDialog.helperText')}
                    </p>
                  </div>
                </div>
              </div>

              <FormField
                control={control}
                name="numberOfSocialAccounts"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel className="text-neutralColor text-sm font-semibold">
                      {t(
                        'public.revealDigitalFootprint.numberOfSocialAccountsLabel',
                      )}
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Share
                          size={20}
                          color={SocialMirrorPalette.neutralColor30}
                          className="absolute left-3 top-1/2 -translate-y-1/2"
                        />
                        <Input
                          className="rounded-xl pl-10 h-11 border-neutralColor10 focus:ring-primaryColor"
                          type="number"
                          min={1}
                          max={99}
                          placeholder={t(
                            'public.revealDigitalFootprint.numberOfSocialAccountsPlaceholder',
                          )}
                          {...field}
                          value={
                            field.value !== undefined ? String(field.value) : ''
                          }
                          onChange={(e) => {
                            const value = e.target.value
                            field.onChange(
                              value === '' ? undefined : parseInt(value, 10),
                            )
                          }}
                        />
                      </div>
                    </FormControl>
                    <p className="text-neutralColor50 text-xs mt-1 leading-relaxed">
                      {t(
                        'public.revealDigitalFootprint.numberOfSocialAccountsHelper',
                      )}
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="email"
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel className="text-neutralColor text-sm font-semibold">
                      {t('public.revealDigitalFootprint.emailLabel')}
                    </FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Sms
                          size={20}
                          color={SocialMirrorPalette.neutralColor30}
                          className="absolute left-3 top-1/2 -translate-y-1/2"
                        />
                        <Input
                          className="rounded-xl pl-10 h-11 border-neutralColor10 focus:ring-primaryColor"
                          type="email"
                          placeholder={t(
                            'public.revealDigitalFootprint.emailPlaceholder',
                          )}
                          {...field}
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="bg-baseColor rounded-xl border border-neutralColor5 p-3 flex flex-col gap-3">
                <FormField
                  control={control}
                  name="termsAccepted"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          className="mt-1.5"
                          checked={field.value === true}
                          onCheckedChange={(checked) => {
                            field.onChange(checked === true ? true : undefined)
                          }}
                        />
                      </FormControl>
                      <div className="leading-snug">
                        <FormLabel className="text-neutralColor70 text-xs font-medium cursor-pointer">
                          {t('public.revealDigitalFootprint.iAgreeTermsPrefix')}
                          <a
                            href={t('common.termsAndConditionsLink')}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primaryColor hover:underline font-bold"
                          >
                            {t('public.revealDigitalFootprint.iAgreeTermsLink')}
                          </a>
                        </FormLabel>
                        <FormMessage />
                      </div>
                    </FormItem>
                  )}
                />

                <FormField
                  control={control}
                  name="privacyPolicyAccepted"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                      <FormControl>
                        <Checkbox
                          className="mt-1.5"
                          checked={field.value === true}
                          onCheckedChange={(checked) => {
                            field.onChange(checked === true ? true : undefined)
                          }}
                        />
                      </FormControl>
                      <div className="leading-snug">
                        <FormLabel className="text-neutralColor70 text-xs font-medium cursor-pointer">
                          {t(
                            'public.revealDigitalFootprint.iAcknowledgePrivacyPrefix',
                          )}
                          <a
                            href={t('common.privacyPolicyLink')}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primaryColor hover:underline font-bold"
                          >
                            {t(
                              'public.revealDigitalFootprint.iAcknowledgePrivacyLink',
                            )}
                          </a>
                          {t(
                            'public.revealDigitalFootprint.iAcknowledgePrivacySuffix',
                          )}
                        </FormLabel>
                        <FormMessage />
                      </div>
                    </FormItem>
                  )}
                />
              </div>

              <Button
                type="submit"
                className="w-full rounded-xl h-12 text-base font-bold shadow-lg hover:shadow-xl transition-all"
                disabled={!isFormValid}
                isLoading={isLoading}
              >
                {t('public.revealDigitalFootprint.analyzeButton')}
              </Button>
            </form>
          </Form>
        </div>

        <p className="text-neutralColor40 text-xs">
          {t('public.revealDigitalFootprint.copyright')}
        </p>
      </div>
    </WeScreen>
  )
}
